// PRE RMA — Logique PURE de la synchronisation (testée) : aucun accès réseau ni stockage.
//  - trouverOrphelinsSynchronises : patients « ✓ Synchronisé » que le serveur ne connaît plus (« fantômes ») ;
//  - bilanReparation : rapport affiché par le bouton « Réparer la synchronisation ».
import type { OperationFile, StatutFile } from './patients.ts';

/** Sous-ensemble d'un élément de la file locale utile à ces calculs (compatible avec LocalConsultation). */
export interface ElementSynchro {
  localId: string;
  operation: OperationFile;
  syncStatus: StatutFile;
  serverId?: string;
  consultationId?: string;
  erreur?: string;
  updatedAt: number;
}

/**
 * Identifiants locaux des éléments « Synchronisé » (création / modification déjà envoyées) dont l'id serveur est ABSENT de la liste
 * que le serveur vient de renvoyer pour la période : supprimés, validés sous un autre identifiant… Ils n'ont plus rien à envoyer ;
 * les laisser affichés provoque « 404 Consultation introuvable » à la modification et une ligne qui « reste » après suppression.
 * `avant` = instant du début de la lecture serveur : un élément synchronisé PENDANT la lecture n'est jamais retenu.
 * Les éléments non synchronisés (PENDING / ERROR / CONFLICT) ne sont JAMAIS retenus : aucune saisie en attente n'est perdue.
 */
export function trouverOrphelinsSynchronises(file: ElementSynchro[], idsServeur: ReadonlySet<string>, avant: number): string[] {
  const out: string[] = [];
  for (const it of file) {
    if (it.syncStatus !== 'SYNCED' || it.operation === 'DELETE' || it.operation === 'DELETE_ALL') continue;
    const id = it.serverId ?? it.consultationId;
    if (!id || idsServeur.has(id) || it.updatedAt >= avant) continue;
    out.push(it.localId);
  }
  return out;
}

export interface ElementBloque { localId: string; statut: 'ERREUR' | 'CONFLIT' | 'ATTENTE'; motif: string }
export interface BilanReparation { envoyes: number; repares: number; bloques: ElementBloque[] }

const MOTIF_ATTENTE = "En attente d'envoi (réseau, session ou compte propriétaire de la saisie). Elle est conservée sur cet appareil.";
const MOTIF_CONFLIT = "Modifiée entre-temps ailleurs : la version du serveur est conservée. Rouvrez la fiche pour repartir de la version à jour.";

/**
 * Compare la file « non synchronisée » AVANT et APRÈS la réparation.
 *  - envoyes : éléments qui ont quitté la file (confirmés par le serveur) ;
 *  - repares : parmi eux, ceux qui étaient « Refusé / À vérifier » avant ;
 *  - bloques : éléments encore présents, avec un motif en français (jamais supprimés par la réparation).
 */
export function bilanReparation(avant: ElementSynchro[], apres: ElementSynchro[]): BilanReparation {
  const restants = new Set(apres.map((i) => i.localId));
  const partis = avant.filter((i) => !restants.has(i.localId));
  return {
    envoyes: partis.length,
    repares: partis.filter((i) => i.syncStatus === 'ERROR').length,
    bloques: apres.map((i): ElementBloque => ({
      localId: i.localId,
      statut: i.syncStatus === 'ERROR' ? 'ERREUR' : i.syncStatus === 'CONFLICT' ? 'CONFLIT' : 'ATTENTE',
      motif: i.erreur || (i.syncStatus === 'CONFLICT' ? MOTIF_CONFLIT : MOTIF_ATTENTE),
    })),
  };
}
